import React, { useCallback, useEffect, useMemo } from "react";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useTheme, alpha } from "@mui/material/styles";
import {
  NOTICE_BODY_VISIBLE_MAX,
  isPlainNoticeBody,
  noticeBodyVisibleLength,
  plainTextToNoticeBodyHtml,
} from "../../utils/noticeBodyHtml";
import { sanitizeNoticeBodyHtml } from "../../utils/sanitizeNoticeBodyHtml";
import { composerPanelBorderSx } from "./composerBorderStyles";

export type RichBodyEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  minRows?: number;
  maxVisibleChars?: number;
  disabled?: boolean;
};

function toEditorHtml(value: string): string {
  if (!value) return "";
  if (isPlainNoticeBody(value)) {
    return plainTextToNoticeBodyHtml(value);
  }
  return sanitizeNoticeBodyHtml(value);
}

const RichBodyEditor: React.FC<RichBodyEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your notice content here...",
  error,
  helperText,
  minRows = 4,
  maxVisibleChars = NOTICE_BODY_VISIBLE_MAX,
  disabled = false,
}) => {
  const theme = useTheme();
  const minHeight = Math.max(minRows, 3) * 24 + 16;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Placeholder.configure({ placeholder }),
    ],
    content: toEditorHtml(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "notice-rich-body-editor",
      },
      handlePaste: (_view, event) => {
        const html = event.clipboardData?.getData("text/html");
        if (!html) return false;
        // TipTap inserts; sanitize runs on update.
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const raw = ed.getHTML();
      const clean = sanitizeNoticeBodyHtml(raw);
      const visible = noticeBodyVisibleLength(clean);
      if (visible > maxVisibleChars) {
        // Revert over-limit edits by restoring previous sanitized value
        ed.commands.setContent(toEditorHtml(value), false);
        return;
      }
      // TipTap empty doc is often <p></p>
      const next =
        noticeBodyVisibleLength(clean) === 0 ? "" : clean;
      if (next !== value) {
        onChange(next);
      }
    },
  });

  // Sync external value (draft restore / template) into editor
  useEffect(() => {
    if (!editor) return;
    const incoming = toEditorHtml(value);
    const current = sanitizeNoticeBodyHtml(editor.getHTML());
    if (
      (incoming || "") !== (current || "") &&
      noticeBodyVisibleLength(incoming) !== noticeBodyVisibleLength(current)
    ) {
      editor.commands.setContent(incoming || "", false);
    } else if ((incoming || "") !== (current || "") && incoming !== current) {
      // Same visible length but different markup (e.g. bold toggle from outside)
      const plainIn = incoming.replace(/<[^>]+>/g, "");
      const plainCur = current.replace(/<[^>]+>/g, "");
      if (plainIn !== plainCur) {
        editor.commands.setContent(incoming || "", false);
      }
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const visibleLen = useMemo(
    () => noticeBodyVisibleLength(value),
    [value],
  );

  const toggle = useCallback(
    (action: () => void) => {
      if (!editor || disabled) return;
      action();
      editor.chain().focus().run();
    },
    [editor, disabled],
  );

  if (!editor) {
    return null;
  }

  const toolBtn = (
    title: string,
    active: boolean,
    onClick: () => void,
    icon: React.ReactNode,
  ) => (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          aria-label={title}
          aria-pressed={active}
          sx={{
            borderRadius: 1,
            color: active ? "primary.main" : "text.secondary",
            bgcolor: active
              ? alpha(theme.palette.primary.main, 0.12)
              : "transparent",
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ width: "100%", mb: helperText ? 0 : 2 }}>
      <Box
        sx={{
          ...composerPanelBorderSx,
          borderColor: error ? "error.main" : "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            px: 0.75,
            py: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            flexWrap: "wrap",
          }}
        >
          {toolBtn(
            "Bold",
            editor.isActive("bold"),
            () => toggle(() => editor.chain().focus().toggleBold().run()),
            <FormatBoldIcon fontSize="small" />,
          )}
          {toolBtn(
            "Italic",
            editor.isActive("italic"),
            () => toggle(() => editor.chain().focus().toggleItalic().run()),
            <FormatItalicIcon fontSize="small" />,
          )}
          {toolBtn(
            "Underline",
            editor.isActive("underline"),
            () =>
              toggle(() => editor.chain().focus().toggleUnderline().run()),
            <FormatUnderlinedIcon fontSize="small" />,
          )}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
          {toolBtn(
            "Bullet list",
            editor.isActive("bulletList"),
            () =>
              toggle(() => editor.chain().focus().toggleBulletList().run()),
            <FormatListBulletedIcon fontSize="small" />,
          )}
          {toolBtn(
            "Numbered list",
            editor.isActive("orderedList"),
            () =>
              toggle(() => editor.chain().focus().toggleOrderedList().run()),
            <FormatListNumberedIcon fontSize="small" />,
          )}
          <Box sx={{ flex: 1 }} />
          <Typography
            variant="caption"
            sx={{
              pr: 1,
              color:
                visibleLen >= maxVisibleChars ? "error.main" : "text.secondary",
            }}
          >
            {visibleLen}/{maxVisibleChars}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            minHeight,
            maxHeight: 280,
            overflowY: "auto",
            "& .notice-rich-body-editor": {
              outline: "none",
              minHeight: minHeight - 16,
              fontSize: theme.typography.body1.fontSize,
              lineHeight: 1.6,
              color: "text.primary",
            },
            "& .notice-rich-body-editor p": {
              margin: "0 0 0.5em",
            },
            "& .notice-rich-body-editor p:last-child": {
              marginBottom: 0,
            },
            "& .notice-rich-body-editor ul, & .notice-rich-body-editor ol": {
              margin: "0.25em 0 0.5em",
              paddingLeft: "1.4em",
            },
            "& .notice-rich-body-editor li": {
              margin: "0.15em 0",
            },
            "& .notice-rich-body-editor u": {
              textDecoration: "underline",
            },
            "& .ProseMirror p.is-editor-empty:first-of-type::before": {
              color: theme.palette.text.disabled,
              content: "attr(data-placeholder)",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>
      {helperText ? (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.75,
            ml: 1.75,
            color: error ? "error.main" : "text.secondary",
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default RichBodyEditor;
